import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid,
} from '@mui/material';
import { ArrowBack, AddCircle, Delete, Save } from '@mui/icons-material';
import { PageHeader, headerCellSx } from '../../components/common/pageKit';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { formatCurrency } from '../../utils/currency';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// Create / edit a Quotation as a dedicated full page (not a modal), mirroring
// SalesContractFormPage / CustomerEnquiryFormPage: titled sections, a dynamic
// line-item grid with live totals, and a sticky bottom action bar. The
// create/update apiClient calls and payload shape are unchanged from the old
// dialog, so the backend (Sales.createQuotation / updateQuotation) is untouched.
// Editing is only legal while a quotation is DRAFT (server-enforced).
// ---------------------------------------------------------------------------

interface Customer {
  id: number | string;
  customer_code?: string;
  company_name: string;
  contact_person?: string;
}

interface Product {
  id: string | number;
  product_name: string;
  hsn_code?: string;
  selling_price?: number;
  gst_rate?: number;
  unit_of_measure?: string;
  is_active?: boolean;
}

let draftKeySeq = 0;
const nextDraftKey = () => `draft-${++draftKeySeq}`;

interface ItemDraft {
  key: string;
  product: Product | null;
  hsnCode: string;
  quantity: string;
  unitPrice: string;
  discountPercent: string;
  gstRate: string;
}

const emptyItem = (): ItemDraft => ({
  key: nextDraftKey(),
  product: null,
  hsnCode: '',
  quantity: '1',
  unitPrice: '0',
  discountPercent: '0',
  gstRate: '18',
});

const toNum = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (days: number) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);

const QuotationFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const c = useControlRoomColors();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [loadingQuotation, setLoadingQuotation] = useState(isEdit);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [quotationNumber, setQuotationNumber] = useState<string | null>(null);
  const [quotationDate, setQuotationDate] = useState(todayISO());
  const [validUntil, setValidUntil] = useState(plusDaysISO(30));
  const [paymentTerms, setPaymentTerms] = useState('');
  const [priceList, setPriceList] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([emptyItem()]);
  const [status, setStatus] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    setLoadingRefData(true);
    Promise.all([
      api.get<{ customers: Customer[] }>('/sales/customers').catch(() => ({ customers: [] as Customer[] })),
      api.get<{ products: Product[] }>('/products').catch(() => ({ products: [] as Product[] })),
    ])
      .then(([customersRes, productsRes]) => {
        setCustomers(customersRes.customers || []);
        setProducts((productsRes.products || []).filter((p) => p.is_active !== false));
      })
      .finally(() => setLoadingRefData(false));
  }, []);

  useEffect(() => {
    if (!isEdit || !id || loadingRefData) return;
    setLoadingQuotation(true);
    api
      .get<{ quotation: any }>(`/sales/quotations/${id}`)
      .then(({ quotation }) => {
        setQuotationNumber(quotation.quotationNumber || null);
        setCustomer(customers.find((cust) => String(cust.id) === String(quotation.customerId)) || null);
        setQuotationDate(quotation.quotationDate ? quotation.quotationDate.slice(0, 10) : todayISO());
        setValidUntil(quotation.validUntil ? quotation.validUntil.slice(0, 10) : '');
        setNotes(quotation.termsConditions || '');
        setStatus(quotation.status);
        setItems(
          (quotation.items || []).length > 0
            ? quotation.items.map((it: any) => {
                const product = products.find((p) => String(p.id) === String(it.productId)) || null;
                return {
                  key: nextDraftKey(),
                  product,
                  hsnCode: it.hsnCode || product?.hsn_code || '',
                  quantity: String(toNum(it.quantity) || 1),
                  unitPrice: String(toNum(it.unitPrice)),
                  discountPercent: String(toNum(it.discountPercent)),
                  gstRate: '18',
                };
              })
            : [emptyItem()]
        );
      })
      .catch((err: any) => setError(err.message || 'Failed to load quotation'))
      .finally(() => setLoadingQuotation(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, loadingRefData]);

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (key: string) => setItems((prev) => prev.filter((it) => it.key !== key));
  const updateItem = (key: string, patch: Partial<ItemDraft>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  const handleProductChange = (key: string, product: Product | null) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.key !== key) return it;
        return {
          ...it,
          product,
          hsnCode: product?.hsn_code || it.hsnCode,
          unitPrice: product?.selling_price != null ? String(product.selling_price) : it.unitPrice,
          gstRate: product?.gst_rate != null ? String(product.gst_rate) : it.gstRate,
        };
      })
    );
  };

  const canEdit = !isEdit || status === 'DRAFT';

  const lineTotal = (it: ItemDraft) => {
    const amount = toNum(it.quantity) * toNum(it.unitPrice);
    const discount = (amount * toNum(it.discountPercent)) / 100;
    const taxable = amount - discount;
    const tax = (taxable * toNum(it.gstRate)) / 100;
    return { taxable, tax, total: taxable + tax };
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    let totalTax = 0;
    items.forEach((it) => {
      const { taxable, tax } = lineTotal(it);
      subtotal += taxable;
      totalTax += tax;
    });
    return { subtotal, totalTax, total: subtotal + totalTax };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!customer) errs.push('Select the customer this quotation is for.');
    if (!quotationDate) errs.push('Quotation date is required.');
    const validItems = items.filter((it) => it.product);
    if (validItems.length === 0) errs.push('Add at least one line item with a product.');
    validItems.forEach((it, idx) => {
      if (toNum(it.quantity) <= 0) errs.push(`Line ${idx + 1}: quantity must be greater than 0.`);
      if (toNum(it.unitPrice) <= 0) errs.push(`Line ${idx + 1}: unit price must be greater than 0.`);
    });
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      // Payload shape is unchanged from the original dialog so the backend
      // (Sales.createQuotation / updateQuotation) needs no changes. paymentTerms
      // and priceList are captured for the user; the notes/terms field persists.
      const payload: Record<string, any> = {
        customerId: customer!.id,
        quotationDate,
        validUntil: validUntil || undefined,
        paymentTerms: paymentTerms.trim() || undefined,
        priceList: priceList.trim() || undefined,
        notes: notes.trim() || '',
        items: items
          .filter((it) => it.product)
          .map((it) => ({
            productId: it.product!.id,
            quantity: toNum(it.quantity),
            unitPrice: toNum(it.unitPrice),
            discountPercent: toNum(it.discountPercent),
            gstRate: toNum(it.gstRate),
          })),
      };

      if (isEdit && id) {
        await api.put(`/sales/quotations/${id}`, payload);
      } else {
        await api.post('/sales/quotations', payload);
      }

      navigate('/sales/quotations');
    } catch (err: any) {
      setError(err.message || 'Failed to save quotation');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = loadingRefData || loadingQuotation;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/quotations')} sx={{ mb: 1 }}>
        Back to Quotations
      </Button>

      <PageHeader
        title={isEdit ? 'Edit Quotation' : 'New Quotation'}
        qualifier={quotationNumber ? `— ${quotationNumber}` : undefined}
        subtitle="Build a priced offer for a customer, with GST worked out per line."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!canEdit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This quotation is {status} and can no longer be edited. Use "Revise" from the list to snapshot and edit a new revision.
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((e) => (
            <Box key={e}>{e}</Box>
          ))}
        </Alert>
      )}

      {/* Section: Customer & Reference */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Customer &amp; Reference
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 5 }}>
            <Autocomplete
              size="small"
              options={customers}
              value={customer}
              onChange={(_, v) => setCustomer(v)}
              getOptionLabel={(o) => o.company_name || ''}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              disabled={!canEdit}
              renderInput={(params) => <TextField {...params} label="Customer *" placeholder="Select customer" />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Quotation Date"
              value={quotationDate}
              onChange={(e) => setQuotationDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Valid Until"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Commercial Terms */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Commercial Terms
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Payment Terms"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g. Net 30, 50% advance"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Price List"
              value={priceList}
              onChange={(e) => setPriceList(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g. Standard, Wholesale"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Line Items */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 2.5 }}>
        <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Line Items
          </Typography>
          {canEdit && (
            <Button size="small" variant="outlined" startIcon={<AddCircle />} onClick={addItem}>
              Add Item
            </Button>
          )}
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Product</TableCell>
                <TableCell sx={headerCellSx}>HSN</TableCell>
                <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                <TableCell sx={headerCellSx} align="right">Rate</TableCell>
                <TableCell sx={headerCellSx} align="right">Disc %</TableCell>
                <TableCell sx={headerCellSx} align="right">GST %</TableCell>
                <TableCell sx={headerCellSx} align="right">Line Total</TableCell>
                {canEdit && <TableCell sx={headerCellSx} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.key} hover>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Autocomplete
                      size="small"
                      options={products}
                      value={it.product}
                      onChange={(_, v) => handleProductChange(it.key, v)}
                      getOptionLabel={(o) => `${o.product_name}${o.hsn_code ? ` (${o.hsn_code})` : ''}`}
                      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                      disabled={!canEdit}
                      renderInput={(params) => <TextField {...params} placeholder="Select product" />}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 90 }}>
                    <TextField
                      size="small"
                      value={it.hsnCode}
                      onChange={(e) => updateItem(it.key, { hsnCode: e.target.value })}
                      disabled={!canEdit}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 80 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={it.quantity}
                      onChange={(e) => updateItem(it.key, { quantity: e.target.value })}
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                      disabled={!canEdit}
                      sx={{ minWidth: 70 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 100 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={it.unitPrice}
                      onChange={(e) => updateItem(it.key, { unitPrice: e.target.value })}
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                      disabled={!canEdit}
                      sx={{ minWidth: 90 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 80 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={it.discountPercent}
                      onChange={(e) => updateItem(it.key, { discountPercent: e.target.value })}
                      inputProps={{ min: 0, max: 100, step: '0.01', style: { textAlign: 'right' } }}
                      disabled={!canEdit}
                      sx={{ minWidth: 70 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 80 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={it.gstRate}
                      onChange={(e) => updateItem(it.key, { gstRate: e.target.value })}
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                      disabled={!canEdit}
                      sx={{ minWidth: 70 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 110 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {formatCurrency(lineTotal(it).total)}
                    </Typography>
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => removeItem(it.key)} disabled={items.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Live totals */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ width: 320 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography color="text.secondary">Subtotal</Typography>
              <Typography fontWeight={600}>{formatCurrency(totals.subtotal)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography color="text.secondary">Tax (GST)</Typography>
              <Typography fontWeight={600}>{formatCurrency(totals.totalTax)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Total
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: c.accent }}>
                {formatCurrency(totals.total)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Section: Notes & Terms */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Notes &amp; Terms
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={3}
          label="Notes / Terms &amp; Conditions"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={!canEdit}
        />
      </Paper>

      {/* Sticky bottom action bar */}
      {canEdit && (
        <Paper
          elevation={0}
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 5,
            mt: 1,
            py: 1.5,
            px: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: c.panel,
          }}
        >
          <Button onClick={() => navigate('/sales/quotations')} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Quotation'}
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default QuotationFormPage;
